import React, { useCallback, useEffect, useState } from 'react';
import cn from 'clsx';
import { useSearch } from '@okapi/router-react';
import { PromoModal, Section, Typography } from 'common/components';
import { strictBreakpoints } from 'common/styles/breakpoints';
import { LPPromoBanner, usePromoBannerSelector, useRegionIdSelector } from 'modules/model-json';
import { createElementKey } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import {
  promoBannerContent,
  promoBannerContentImage,
  promoBannerContentTitle,
  promoBannerLogo,
  PromoBannerOffer,
  promoBannerOfferImage,
  promoBannerOfferPlate,
  promoBannerOffersGrid,
  PromoBannerOffersGridMap,
  promoBannerOfferText,
  promoBannerOfferTitle,
  promoBannerOfferWrapper,
  promoBannerText,
  StyledContainer,
} from './promo-banner-styles';

type TProps = {
  elementBlock: string;
  onModalBtnClick?: TFunction;
};

export const PromoBanner: React.FC<TProps> = ({ elementBlock, onModalBtnClick }) => {
  const [searchParams] = useSearch();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeOfferIndex, setActiveOfferIndex] = useState<number>(0);

  const promoBanner: LPPromoBanner | null = usePromoBannerSelector();
  const regionId = useRegionIdSelector();

  useEffect(() => {
    promoBanner?.offers?.forEach(({ name }, index: number) => {
      if (name && searchParams.offer === name) {
        setActiveOfferIndex(index);
        setIsModalOpen(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!promoBanner) {
    return null;
  }

  const handleBannerClick = (index: number) => () => {
    setIsModalOpen(true);
    setActiveOfferIndex(index);
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock,
      elementName: 'Промопредложение',
      elementText: promoBanner.offers[index].title,
    });
  };

  return (
    <Section>
      <StyledContainer bgColor={promoBanner.bgColor}>
        <div className={promoBannerContent}>
          <div>
            {promoBanner.logo && <img className={promoBannerLogo} src={promoBanner.logo} alt="Логотип" />}
            <Typography className={promoBannerContentTitle} variant="h1" size="extra">{promoBanner.title}</Typography>
            {promoBanner.text && <Typography className={promoBannerText} variant="h2">{promoBanner.text}</Typography>}
          </div>
          <div>
            <picture>
              <source srcSet={promoBanner.photoLinkLarge} media={strictBreakpoints['--only-largeDesktop']} />
              <source srcSet={promoBanner.photoLinkDesktop} media={strictBreakpoints['--only-desktop']} />
              <source srcSet={promoBanner.photoLinkTable} media={strictBreakpoints['--only-tablet']} />
              <img alt="Изображение для баннера" className={promoBannerContentImage} src={promoBanner.photoLinkMobile} />
            </picture>
          </div>
        </div>

        <div className={cn(promoBannerOffersGrid, PromoBannerOffersGridMap[promoBanner.offers.length])}>
          {promoBanner.offers.map((offer, index: number) => (
            <PromoBannerOffer key={createElementKey('promo-banner-info', index)} onClick={handleBannerClick(index)}>
              <div className={promoBannerOfferWrapper}>
                <div>
                  <Typography
                    className={promoBannerOfferTitle}
                    data-variant="title"
                    variant="h3"
                    size="extra"
                  >
                    {offer.title}
                  </Typography>
                  {offer.text && (
                    <Typography
                      className={promoBannerOfferText}
                      data-variant="description"
                      variant="m" size="body_short"
                    >
                      {offer.text}
                    </Typography>
                  )}
                </div>
              </div>
              <img alt={'alt'} data-variant="img" className={promoBannerOfferImage} src={offer.photoLink} />
              {offer.plateLink && (
                <img alt={'alt'} data-variant="plate" className={promoBannerOfferPlate} src={offer.plateLink} />
              )}
            </PromoBannerOffer>
          ))}
        </div>

      </StyledContainer>

      {isModalOpen && (
        <PromoModal
          elementBlock={elementBlock}
          elementName={`${promoBanner.offers[activeOfferIndex].title} / modal`}
          modalContent={promoBanner.offers[activeOfferIndex].modal}
          isOpen={isModalOpen}
          onModalBtnClick={onModalBtnClick}
          onClose={handleCloseModal}
          regionId={regionId}
        />
      )}
    </Section>
  );
}
